import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
  ColumnLabels, PrimusSolrDataTableComponent,
  RefreshDataFn,
  RowTransformFn
} from '../../../shared/primus-tables/primus-solr-data-table/primus-solr-data-table.component';
import {TreeNode} from '../../../shared/primus-solr-tree-view/tree-node/tree-node.component';
import {
  AdminLocationPlaceFormModalComponent,
  AdminLocationFormModalData
} from '../admin-location-place-form-modal/admin-location-place-form-modal.component';
import {ConceptDialogService} from "../../admin-concept-lists/concept-dialog.service";
import {ConceptsContainer} from "../../../core/definitions/concepts-container";
import {CmsApiService} from "../../../core/cms-api.service";
import {ObjectTypes} from "../../../core/definitions/object-types";
import {ConceptService} from "../../../core/concept.service";
import {FeatureFlagsService} from "../../../core/feature-flags.service";


@Component({
  selector: 'app-admin-place',
  templateUrl: './admin-place.component.html',
  styleUrls: ['./admin-place.component.scss'],
  standalone: false
})
export class AdminPlaceComponent implements OnInit {
  // Injections
  private readonly conceptDialogService: ConceptDialogService = inject(ConceptDialogService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly conceptService: ConceptService = inject(ConceptService);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly modal: MatDialog = inject(MatDialog);

  @ViewChild(PrimusSolrDataTableComponent) tableComponent: PrimusSolrDataTableComponent;

  readonly topNodeLabel: string = 'TRANS__ADMIN__PLACE__TOP_NODE_LABEL';

  columnLabels: ColumnLabels = {
    'place_name.name': 'TRANS__ADMIN__PLACE__TABLE_COLUMN__PLACE',
    place_type_id_value: 'TRANS__ADMIN__PLACE__TABLE_COLUMN__TYPE',
    identification_number: 'TRANS__ADMIN__PLACE__TABLE_COLUMN__ID',
    'collection.collection_id_value': 'TRANS__FIELD__COLLECTION',
    valid: 'TRANS__ADMIN__PLACE__TABLE_COLUMN__VALID',
    authority_dataset_id_value: 'TRANS__ADMIN__PLACE__TABLE_COLUMN__AUTHORITY_DATASET',
    created_at: 'TRANS__ADMIN__PLACE__TABLE_COLUMN__CREATED_AT',
    updated_at: 'TRANS__ADMIN__PLACE__TABLE_COLUMN__UPDATED_AT',
    'description.description': 'TRANS__ADMIN__PLACE__TABLE_COLUMN__DESCRIPTION'

  };

  columns: Array<string> = [
    'place_name.name',
    'place_type_id_value',
    'identification_number.identification_number',
    'collection.collection_id_value',
    'valid',
    'authority_dataset_id_value',
    'created_at',
    'updated_at',
    'description.description'
  ];

  readonly rowTransformer: RowTransformFn = (
    (value, _, col) => col === 'valid' ? !value : value
  );
  readonly parentIdParentQueryPart = `{!parent which=object_type:${ObjectTypes.PLACE}}object_type:${ObjectTypes.MATERIALIZED_PATH_ITEM} AND `
  readonly parentIdProp: string = 'materialized_paths.parent_id';
  readonly parentIdFlProp: string = `[child parentFilter=object_type:${ObjectTypes.PLACE} childFilter=object_type:${ObjectTypes.MATERIALIZED_PATH_ITEM} childFields=place.paths.level]`;
  parentLabelProp: string = 'place_name.name';
  readonly baseQuery = 'object_type:place';
  filterQuery?: string;
  currentFilterString = '';
  currentQuery: string;

  parentNavigationView: unknown;

  selectedParentNode: TreeNode | null = null;
  selectedPlaces: string[];
  placeKulturNavImport: boolean;

  constructor() {
    this.updateQuery();
  }

  async ngOnInit(): Promise<void> {
    this.placeKulturNavImport = true;
    this.parentNavigationView = {
      path: 'admin',
      adminType: 'place',
      hasSubMenus: true,
      target_params: {admin_type: 'place'},
      currentState: 'places'
    };
    if (!this.featureFlagsService.getFeatureFlags().temporary.newNameFields) {
      // Switching to old name field
      this.parentLabelProp = 'place_name.name';
      this.columnLabels[this.parentLabelProp] = 'TRANS__ADMIN__PLACE__TABLE_COLUMN__PLACE';
      this.columns = [
        this.parentLabelProp,
        'place_type_id_value',
        'identification_number.identification_number',
        'collection.collection_id_value',
        'valid',
        'authority_dataset_id_value',
        'created_at',
        'updated_at',
        'description.description'
      ];
    }
  }

  handleSelectionChanged(event: any) {
    this.selectedPlaces = event;
  }

  async handleEditOrCreatePlaceClicked(
    refreshData: RefreshDataFn, placeId?: string, kulturNavImport?: boolean): Promise<void> {
    if (kulturNavImport) {
      await this.getConceptsFromKulturNav(refreshData);
    } else {
      await this.openEditDialog(refreshData, placeId);
    }
  }

  private async openEditDialog(refreshData: RefreshDataFn, placeId: string): Promise<void> {
    return new Promise((resolve) => {
      const placeModal = this.modal.open(AdminLocationPlaceFormModalComponent, {
        panelClass: 'edit-dialog',
        disableClose: true,
        data: {
          id: placeId,
          objectType: 'place',
          parentId: this.selectedParentNode?.id,
          headingCreateNew: 'TRANS__ADMIN__PLACE__CREATE_NEW_HEADING',
          headingEditExisting: 'TRANS__ADMIN__PLACE__EDIT_EXISTING_HEADING',
          currentLevelName: this.selectedParentNode?.label || this.topNodeLabel,
        } as AdminLocationFormModalData
      });
      placeModal.afterClosed().subscribe((result) => {
        if (result) {
          if (refreshData) {
            refreshData();
          }
          resolve(result);
        }
      });
    });
  }

  async getConceptsFromKulturNav(refreshData: RefreshDataFn) {
    const conceptType = await this.conceptService.getConceptType(ObjectTypes.PLACE);
    const conceptsContainer = new ConceptsContainer(this.cms)
    conceptsContainer.conceptType = conceptType;
    this.conceptDialogService.openKulturNavImporter(conceptsContainer, (hadImports: boolean) => {
      if (hadImports) {
        refreshData();
      }
    });
  }


  updateQuery(): void {
    const q = [this.baseQuery];
    if (this.selectedParentNode) {
      q.push(`(${this.parentIdParentQueryPart}${this.parentIdProp}:"${this.selectedParentNode.id}")`);
    } else {
      q.push(`(${this.parentIdParentQueryPart}-${this.parentIdProp}:*)`);
    }
    if (this.filterQuery) {
      q.push(this.filterQuery);
    }
    this.currentQuery = `${q.join(' AND ')}`;
  }

  setFilterQuery(fq: string = ''): void {
    this.filterQuery = fq;
    this.updateQuery();
  }

  selectParent(node: TreeNode | null): void {
    this.selectedParentNode = node;
    this.updateQuery();
  }

  onActionPerformed() {
    this.selectedPlaces = [];
    this.tableComponent.dataSource.clearSelected();
    this.tableComponent.dataSource.refreshData();
  }
}
